import React, { useEffect, useState } from 'react';
import C from '../colors';

const LEVEL_NAMES = { 0: '试用', 1: '会员', 2: '高级会员' };

const OPTION_TEXT = {
  1080: { name: '标清', hint: '手机上能看清构图和表情，放大后偏糊。最不容易被截图拿去用。' },
  1920: { name: '高清', hint: '手机全屏清晰，放大能看出闭眼、虚焦。适合大多数选片。' },
  2560: { name: '超清', hint: '放大检查细节也很清楚。适合对画质要求高的客户。' },
};

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12 5 5 9-10"/>
    </svg>
  );
}

// Customer-view resolution, laid out like the watermark and tag settings it
// sits beside. Selection updates immediately; a failed save rolls it back.
function PreviewResolutionPanel({ scope = 'order', value, legacy = false, capability, onSave, onLocked }) {
  const [selected, setSelected] = useState(value ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelected(value ?? null); }, [value]);

  const options = capability?.options || [];

  const choose = async (option) => {
    if (!option.allowed) { onLocked?.(option); return; }
    if (option.value === selected || saving) return;
    const previous = selected;
    setSelected(option.value);
    setSaving(true);
    try {
      await onSave(option.value);
    } catch (error) {
      setSelected(previous);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      <div style={{ padding: '24px 36px 80px', maxWidth: 880 }}>
        <h3 style={{ margin: 0, fontFamily: C.serif, fontWeight: 400, fontSize: 22, color: C.ink }}>客户看图清晰度</h3>
        <p style={{ margin: '6px 0 20px', fontSize: 13, color: C.ink3, lineHeight: 1.6 }}>
          客户选片时看到的大图尺寸（长边像素）。清晰度越低，客户越难截图直接拿去用。
          {scope === 'global'
            ? ' 这里是新建订单的默认值，每个订单还可以在"清晰度"页签里单独调整。'
            : ' 这里只影响这个订单。'}
        </p>

        {scope === 'order' && legacy && (
          <div data-testid="preview-resolution-legacy" style={{ marginBottom: 16, padding: '12px 14px', background: C.paper2, border: `1px solid ${C.hair}`, borderRadius: 10, fontSize: 12, color: C.ink2 }}>
            这个订单是之前创建的，目前沿用上传照片时的清晰度（下方已选中）。换一档即可立即更新客户看到的图片。
          </div>
        )}

        <div data-testid="preview-resolution-picker" role="radiogroup" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {options.map(option => {
            const on = selected === option.value;
            const text = OPTION_TEXT[option.value] || { name: String(option.value), hint: '' };
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={on}
                data-testid={`preview-resolution-${option.value}`}
                onClick={() => choose(option)}
                style={{
                  position: 'relative', textAlign: 'left', padding: '16px 16px 14px',
                  borderRadius: 12, cursor: option.allowed ? 'pointer' : 'not-allowed',
                  border: `2px solid ${on ? C.brand : C.hair}`,
                  background: on ? C.brandSoft : '#fff',
                  opacity: option.allowed ? 1 : 0.6,
                  fontFamily: C.sans, transition: 'border-color .15s, background .15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 16, fontWeight: 600, color: C.ink }}>{text.name}</span>
                  {on
                    ? <span style={{ width: 22, height: 22, borderRadius: 11, background: C.brand, display: 'grid', placeItems: 'center' }}><Check/></span>
                    : !option.allowed && <span style={{ fontSize: 11, color: C.brand2, fontWeight: 500 }}>{LEVEL_NAMES[option.requiredLevel] || '升级'}可用</span>}
                </div>
                <div style={{ marginTop: 4, fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>长边 {option.value} 像素</div>
                <div style={{ marginTop: 8, fontSize: 12, color: C.ink2, lineHeight: 1.55 }}>{text.hint}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PreviewResolutionPanel;
